import { useState } from "react";
import { motion } from "framer-motion";
import { Crown, Check, Zap, Calendar, ArrowUpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useRole } from "@/contexts/RoleContext";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageTour } from "@/components/tour/PageTour";
import { packageDetailsSteps } from "@/lib/tour-steps";

const standardFeatures = [
  "Meta (Facebook & Instagram) Ads campaign management",
  "15 high-converting creative assets",
  "U.S.-based professional call team",
  "CRM lead capture via GoHighLevel",
  "Lead nurturing sequences (email + SMS)",
  "Automated demo booking calendar",
  "Weekly performance reports",
  "Monthly strategy calls",
];

const eliteFeatures = [
  "Everything in Standard, plus:",
  "Meta, Google, AND LinkedIn Ads",
  "UNLIMITED creative production",
  "Human sales closers handle your demos",
  "AI-powered lead scoring",
  "Multi-touch attribution tracking",
  "Dedicated account manager",
  "Priority Slack channel",
  "Bi-weekly strategy sessions",
  "Automated customer retention campaigns",
];

export default function PackageDetails() {
  const { clientPackage } = useRole();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUpgradeRequest = async () => {
    if (!upgradeReason.trim()) {
      toast.error("Please provide a reason for the upgrade request");
      return;
    }

    setIsSubmitting(true);

    try {
      // Get the current client info
      const { data: { user } } = await supabase.auth.getUser();
      const clientEmail = user?.email || '';

      // Look up real client record
      const { data: client } = await supabase
        .from('clients')
        .select('id, company_name')
        .eq('email', clientEmail)
        .single();

      if (client) {
        // Update the client's metadata to flag the upgrade request
        await supabase
          .from('clients')
          .update({
            metadata: {
              upgrade_requested: true,
              upgrade_reason: upgradeReason,
              upgrade_requested_at: new Date().toISOString(),
            }
          })
          .eq('id', client.id);
      }
    } catch (err) {
      console.error('Upgrade request error:', err);
    }

    setIsSubmitting(false);
    setIsUpgradeModalOpen(false);
    setUpgradeReason("");
    
    toast.success("Upgrade request submitted! We'll contact you within 24 hours.");
  };

  const isElite = clientPackage === 'elite';

  return (
    <>
    <PageTour steps={packageDetailsSteps} />
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Package Details</h1>
        <p className="text-muted-foreground">View your current package and what's included</p>
      </div>

      {/* Current Package */}
      <VentureCard className={isElite ? "border-amber-500/30" : "border-secondary/30"}>
        <VentureCardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${isElite ? 'bg-amber-500/20' : 'bg-blue-500/20'}`}>
                {isElite ? (
                  <Crown className="w-6 h-6 text-amber-400" />
                ) : (
                  <Zap className="w-6 h-6 text-blue-400" />
                )}
              </div>
              <div>
                <VentureCardTitle>{isElite ? 'Elite Package' : 'Standard Package'}</VentureCardTitle>
                <BadgeVenture variant={isElite ? 'warning' : 'info'} className="mt-1">
                  Current Plan
                </BadgeVenture>
              </div>
            </div>
            {!isElite && (
              <Button onClick={() => setIsUpgradeModalOpen(true)}>
                <ArrowUpCircle className="w-4 h-4 mr-2" />
                Request Upgrade
              </Button>
            )}
          </div>
        </VentureCardHeader>
        <VentureCardContent>
          {/* Pricing */}
          <div className="flex items-baseline gap-2 mb-6 pb-6 border-b border-border">
            <span className="text-4xl font-bold">{isElite ? '$5,150' : '$3,150'}</span>
            <span className="text-muted-foreground">setup</span>
            <span className="text-2xl font-bold ml-4">+ ${isElite ? '200' : '150'}</span>
            <span className="text-muted-foreground">/qualified demo</span>
          </div>

          {/* Features */}
          <div className="space-y-3">
            <h3 className="font-semibold mb-4">What's Included:</h3>
            {(isElite ? eliteFeatures : standardFeatures).map((feature, index) => (
              <div key={index} className="flex items-start gap-3">
                <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isElite ? 'text-amber-400' : 'text-blue-400'}`} />
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </VentureCardContent>
      </VentureCard>

      {/* Billing Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <VentureCard>
          <VentureCardContent className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-5 h-5 text-secondary" />
              <span className="text-sm text-muted-foreground">Billing Cycle</span>
            </div>
            <p className="text-xl font-bold">Weekly</p>
            <p className="text-sm text-muted-foreground">Every 7 days</p>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <Check className="w-5 h-5 text-green-400" />
              <span className="text-sm text-muted-foreground">First 3 Demos</span>
            </div>
            <p className="text-xl font-bold text-green-400">FREE</p>
            <p className="text-sm text-muted-foreground">Goodwill gesture</p>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <Crown className="w-5 h-5 text-amber-400" />
              <span className="text-sm text-muted-foreground">Guarantee</span>
            </div>
            <p className="text-xl font-bold">90 Days</p>
            <p className="text-sm text-muted-foreground">30 qualified demos min</p>
          </VentureCardContent>
        </VentureCard>
      </div>

      {/* Upgrade Modal */}
      <Dialog open={isUpgradeModalOpen} onOpenChange={setIsUpgradeModalOpen}>
        <DialogContent className="bg-surface border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpCircle className="w-5 h-5 text-amber-400" />
              Request Package Upgrade
            </DialogTitle>
            <DialogDescription>
              Upgrade from Standard to Elite package for enhanced features and dedicated support.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg border border-border bg-surface/50">
              <div className="flex justify-between items-center mb-2">
                <span className="text-muted-foreground">Current Package:</span>
                <BadgeVenture variant="info">Standard</BadgeVenture>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Upgrade To:</span>
                <BadgeVenture variant="warning">Elite</BadgeVenture>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">Reason for upgrade</Label>
              <Textarea
                id="reason"
                placeholder="Tell us why you'd like to upgrade and what goals you're looking to achieve..."
                value={upgradeReason}
                onChange={(e) => setUpgradeReason(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsUpgradeModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpgradeRequest} disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
    </>
  );
}
